import React from "react";
import TitleStripe from "../../common/TitleStripe";
import { formatToReal } from "../../../utils/utils";

function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

// Formato compacto: 26900000 -> "R$ 26,9 mi"
function formatCompact(value) {
  const n = toNumber(value);
  if (n === null) return "—";
  const abs = Math.abs(n);
  if (abs >= 1_000_000_000)
    return `R$ ${(n / 1_000_000_000).toLocaleString("pt-BR", {
      maximumFractionDigits: 1,
    })} bi`;
  if (abs >= 1_000_000)
    return `R$ ${(n / 1_000_000).toLocaleString("pt-BR", {
      maximumFractionDigits: 1,
    })} mi`;
  if (abs >= 1_000)
    return `R$ ${(n / 1_000).toLocaleString("pt-BR", {
      maximumFractionDigits: 0,
    })} mil`;
  return formatToReal(n);
}

const PORTES = [
  { label: "Microempresa", max: 360_000 },
  { label: "Pequeno porte", max: 4_800_000 },
  { label: "Médio porte", max: 300_000_000 },
  { label: "Grande porte", max: Infinity },
];

function classificarPorte(anual) {
  if (anual === null) return -1;
  return PORTES.findIndex((p) => anual < p.max);
}

export default function FaturamentoEstimadoAtualPJQuery({ data }) {
  if (!data) return null;

  const source = data?.faturamentoEstimadoAtualPJ ?? data;
  const statusRetorno = Number(source?.statusRetorno ?? data?.statusRetorno);
  if (statusRetorno !== 1) return null;

  const indicador = toNumber(source?.indicador);
  const temValor = indicador !== null && indicador != 0;

  const anual = temValor ? indicador * 12 : null;
  const diario = temValor ? indicador / 22 : null;
  const porteIdx = classificarPorte(anual);

  return (
    <section className="mt-6" id="faturamento-estimado-atual-pj">
      <TitleStripe>Faturamento Estimado Atual</TitleStripe>

      {!temValor ? (
        <div className="mt-4 flex items-center gap-3 rounded-xl border border-dashed border-slate-200 bg-slate-50/70 px-4 py-3.5">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-400">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-5 w-5"
              aria-hidden="true"
            >
              <path d="M3 3v18h18" />
              <path d="M7 15l3-4 3 2 4-6" />
            </svg>
          </span>
          <div>
            <p className="text-sm font-semibold text-slate-600">
              Sem estimativa disponível
            </p>
            <p className="text-[12px] leading-snug text-slate-400">
              Não há dados transacionais suficientes para estimar o faturamento
              atual deste CNPJ.
            </p>
          </div>
        </div>
      ) : (
        <div className="mt-4 flex overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          {/* Barra de acento */}
          <div className="w-1 shrink-0 bg-emerald-500" />

          <div className="flex-1 p-4">
            {/* Cabeçalho: valor + porte */}
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
                  Faturamento mensal estimado
                </p>
                <p className="mt-0.5 text-3xl font-bold tabular-nums text-slate-900">
                  {formatToReal(indicador)}
                </p>
              </div>

              {porteIdx >= 0 && (
                <span className="shrink-0 rounded-md bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200">
                  {PORTES[porteIdx].label}
                </span>
              )}
            </div>

            {/* Métricas derivadas */}
            <div className="mt-2 flex flex-wrap gap-x-6 gap-y-0.5 text-[12px] text-slate-500">
              <span>
                Anual:{" "}
                <b className="font-semibold text-slate-700">
                  {formatCompact(anual)}
                </b>
              </span>
            </div>

            {/* Enquadramento por porte */}
            {porteIdx >= 0 && (
              <div className="mt-3">
                <div className="flex gap-1">
                  {PORTES.map((p, i) => (
                    <div
                      key={p.label}
                      className={`h-1.5 flex-1 rounded-full ${
                        i <= porteIdx ? "bg-emerald-500" : "bg-slate-200"
                      }`}
                    />
                  ))}
                </div>
                <div className="mt-1 flex justify-between">
                  {PORTES.map((p, i) => (
                    <span
                      key={p.label}
                      className={`text-[10px] ${
                        i === porteIdx
                          ? "font-semibold text-emerald-700"
                          : "text-slate-400"
                      }`}
                    >
                      {p.label}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <p className="mt-2 text-[10px] text-slate-400">
              Base: histórico transacionado de recebimentos (boletos, TEDs e
              cartão) dos últimos 3 meses. Valor aproximado para análise de
              crédito.
            </p>
          </div>
        </div>
      )}
    </section>
  );
}
